<template>
  <div class="algebra-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ displayFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <!-- 背景网格 -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 模式 1: 代数天平 -->
           <g v-if="mode === 'algebra'">
              <line x1="400" y1="480" x2="400" y2="380" stroke="#475569" stroke-width="8" />
              <polygon points="400,380 370,420 430,420" fill="#64748b" />
              
              <!-- 天平横梁 -->
              <g :transform="`rotate(${balanceAngle}, 400, 380)`">
                <line x1="150" y1="380" x2="650" y2="380" stroke="#94a3b8" stroke-width="10" stroke-linecap="round" />
                
                <!-- 左托盘 -->
                <g :transform="`translate(150, 0)`">
                   <line x1="0" y1="0" x2="0" y2="100" stroke="#64748b" stroke-width="4" />
                   <rect x="-100" y="100" width="200" height="25" fill="rgba(37, 99, 235, 0.4)" stroke="#3b82f6" stroke-width="3" rx="6" />
                   <text x="0" y="80" fill="#60a5fa" font-size="34" text-anchor="middle" font-weight="900">{{ leftExpr }}</text>
                </g>

                <!-- 右托盘 -->
                <g :transform="`translate(650, 0)`">
                   <line x1="0" y1="0" x2="0" y2="100" stroke="#64748b" stroke-width="4" />
                   <rect x="-100" y="100" width="200" height="25" fill="rgba(239, 68, 68, 0.4)" stroke="#ef4444" stroke-width="3" rx="6" />
                   <text x="0" y="80" fill="#f87171" font-size="34" text-anchor="middle" font-weight="900">{{ rightVal.toFixed(0) }}</text>
                </g>
              </g>
           </g>

           <!-- 模式 2: 数论等式 -->
           <g v-if="mode === 'numbers'">
              <text x="400" y="180" fill="#f1f5f9" font-size="96" text-anchor="middle" font-weight="900" style="text-shadow: 0 0 15px rgba(255,255,255,0.2)">N = {{ numberN }}</text>
              <text x="400" y="280" fill="#94a3b8" font-size="28" text-anchor="middle" font-weight="bold">质因数分解 (算术基本定理):</text>
              <text x="400" y="360" fill="#22d3ee" font-size="56" text-anchor="middle" font-weight="900" font-family="monospace">{{ primeFactors }}</text>
              
              <!-- 演示网格 -->
              <g v-for="i in Math.min(numberN, 60)" :key="'mn'+i">
                 <rect :x="100 + ((i-1)%15) * 42" :y="440 + Math.floor((i-1)/15)*45" width="30" height="30" rx="6"
                         :fill="(i%3 === modRem) ? '#10b981' : '#1e293b'" 
                         :stroke="(i%3 === modRem) ? '#34d399' : '#334155'" stroke-width="2" />
              </g>
           </g>
           
           <!-- 模式 3: 集合逻辑 -->
           <g v-if="mode === 'logic'">
              <circle cx="300" cy="300" r="180" fill="rgba(37, 99, 235, 0.15)" stroke="#3b82f6" stroke-width="6" />
              <text x="220" y="280" fill="#60a5fa" font-size="36" font-weight="900">集合 A</text>
              <circle cx="500" cy="300" r="180" fill="rgba(239, 68, 68, 0.15)" stroke="#ef4444" stroke-width="6" />
              <text x="580" y="280" fill="#fca5a5" font-size="36" font-weight="900">集合 B</text>
              
              <!-- 交集 -->
              <path d="M 400,140 A 180,180 0 0,1 400,460 A 180,180 0 0,1 400,140" fill="rgba(255,255,255,0.1)" />
              <text x="400" y="310" fill="#ffffff" font-size="28" font-weight="bold" text-anchor="middle">交集 (A ∩ B)</text>
           </g>
           
           <text x="50" y="550" fill="rgba(255,255,255,0.3)" font-size="16" font-weight="bold">代数逻辑动态演示</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>研究专题</label>
              <span class="val">{{ modeName }}</span>
           </div>
           <div class="hud-item cyan" v-if="mode === 'algebra'">
              <label>天平状态</label>
              <span class="val">{{ balanceAngle === 0 ? '静态平衡' : '平衡修正中' }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="mode-selector-row">
                 <span class="label">知识点模块：</span>
                 <el-radio-group v-model="mode" size="small" class="full-row-radio">
                    <el-radio-button label="algebra">方程天平</el-radio-button>
                    <el-radio-button label="numbers">数论结构</el-radio-button>
                    <el-radio-button label="logic">集合逻辑</el-radio-button>
                 </el-radio-group>
              </div>

              <div class="control-grid-mini">
                 <div class="s-item" v-if="mode === 'algebra'">
                    <label>方程参数 x 调节 (A+B=C)</label>
                    <el-slider v-model="leftVal" :min="1" :max="100" size="small"></el-slider>
                 </div>
                 <div class="s-item" v-if="mode === 'numbers'">
                    <label>研究整数 N ({{ numberN }})</label>
                    <el-slider v-model="numberN" :min="2" :max="100" size="small"></el-slider>
                 </div>
                 <div class="s-item" v-if="mode === 'logic'">
                    <label>集合空间交叠度控制</label>
                    <el-slider v-model="leftVal" :min="1" :max="100" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p><strong>数学释义：</strong>{{ modeDesc }}。{{ tipText }}</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AlgebraViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'algebra',
      leftVal: 50,
      rightVal: 50,
      numberN: 42,
      timer: null
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    },
    leftVal() {
      if (this.mode === 'algebra') {
         if (this.timer) clearTimeout(this.timer);
         this.timer = setTimeout(() => {
           if(Math.abs(this.leftVal - this.rightVal) > 0.1) {
               this.rightVal += (this.leftVal - this.rightVal) * 0.3;
           } else {
               this.rightVal = this.leftVal;
           }
         }, 80);
      }
    }
  },
  computed: {
    topicIntro() {
       const maps = {
         algebra: '代数方程：通过天平模型探究等式的维护与求解',
         numbers: '数论探索：整数的分解、同余及离散结构的特性',
         logic: '集合逻辑：研究抽象空间的包含、交叠与分类规律'
       };
       return maps[this.mode] || '代数关系仿真';
    },
    modeName() {
       const maps = { algebra: '代数方程', numbers: '数论探索', logic: '集合逻辑' };
       return maps[this.mode];
    },
    displayFormula() {
       if (this.mode === 'algebra') return 'ax + b = c (等式性质)';
       if (this.mode === 'numbers') return 'N = p₁ᵃ¹ p₂ᵃ²... (质因数分解)';
       return 'A ∩ B (集合运算)';
    },
    leftExpr() { return `x + ${ (100 - this.leftVal).toFixed(0) }`; },
    balanceAngle() { return (this.leftVal - this.rightVal) * 0.8; },
    modRem() { return this.numberN % 3; },
    primeFactors() {
       let n = parseInt(this.numberN);
       let factors = [];
       let d = 2;
       while (n > 1) {
          while (n % d === 0) {
             factors.push(d);
             n /= d;
          }
          d++;
          if (d*d > n) { if (n > 1) factors.push(n); break; }
       }
       return factors.join(' × ') || '质数';
    },
    modeDesc() {
       const maps = {
          algebra: '方程是数学的天平，通过等式性质维护左右平衡。任何一端的改变都会引发系统的实时修正',
          numbers: '自然数是代数的原子，算术基本定理揭示了每个合数都能唯一分解为质数的乘积',
          logic: '集合是逻辑的容器，通过交集 我们可以找到属于两个领域的共同特征'
       };
       return maps[this.mode];
    },
    tipText() {
       return '实时调整数值滑块，观察系统如何在动态变化中维持逻辑一致性。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/数论|质数|整除|素|余/.test(kp)) {
         this.mode = 'numbers';
      } else if (/集合|逻辑|真值|命题/.test(kp)) {
         this.mode = 'logic';
      } else {
         this.mode = 'algebra';
      }
    }
  }
}
</script>

<style scoped lang="scss">
.algebra-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; color: #1e293b; font-weight: 700; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 5px 16px; border-radius: 20px; font-weight: 900; font-family: "Courier New", monospace; font-size: 14px; } }
.simulation-stage { flex: 1; min-height: 380px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 15px; right: 15px; display: flex; flex-direction: column; gap: 10px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 10px; border: 1px solid #334155; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 900; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 900; } &.blue { border-left: 4px solid #3b82f6; } &.cyan { border-left: 4px solid #10b981; } } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 14px 18px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; }
.mode-selector-row { display: flex; align-items: center; gap: 10px; .label { font-size: 13px; color: #475569; font-weight: 900; } .full-row-radio { flex: 1; display: flex; ::v-deep .el-radio-button__inner { flex: 1; width: 100%; font-weight: 900; } } }
.control-grid-mini { display: grid; grid-template-columns: 1fr; gap: 15px; .s-item { label { font-size: 12px; color: #475569; font-weight: 900; margin-bottom: 4px; display: block; } } }
.discovery-card-compact { background: #f8fafc; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 12px; i { color: #3b82f6; font-size: 1.2rem; } .discovery-content { flex: 1; p { margin: 0; font-size: 13px; color: #334155; line-height: 1.6; strong { color: #0f172a; font-weight: 900; } } } }
</style>
